import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, FormGroup, Label, Input, Button, Alert } from 'reactstrap';
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents';
import { addData } from '../../lib/taskm';
import { getInstructorStudent } from '../../lib/instructorstudentview';
import { getInstructorIdData } from '../../lib/classview';
import { getDataWithClass } from '../../lib/classdetailview';
import { TextEditor } from '../Common/TextEditor';
import { TaskTopic911Select, TaskTopic12Select } from '../Common/FormSelects';
import { getCurrentDateInput } from '../../utils/dateTimeUtils';

const InstructorTaskModal = ({ isOpen, toggle, instructorId, onSave, isDarkMode }) => {
    const [selectedStudent, setSelectedStudent] = useState('All');
    const [selectedClass, setSelectedClass] = useState('');
    const [desc, setDesc] = useState('');
    const [category, setCategory] = useState('Student');
    const [topicType, setTopicType] = useState('Grade911');
    const [instructorStudent, setInstructorStudent] = useState([]);
    const [instructorClass, setInstructorClass] = useState([]);
    const [classDetail, setClassDetail] = useState([]);
    const [taskTopic, setTaskTopic] = useState('');
    const [date, setDate] = useState(getCurrentDateInput());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [showToast, setShowToast] = useState(false);

    const showToastMessage = (message) => {
        setToastMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    }

    useEffect(() => {
        if (isOpen) {
            setDesc('');
            setTaskTopic('');
            setDate(getCurrentDateInput());
            if (instructorId) {
                fetchInstructorData();
            }
            setError('');
        }
    }, [isOpen, instructorId]);

    const fetchInstructorData = async () => {
        try {
            const students = await getInstructorStudent(instructorId);
            setInstructorStudent(students);

            const clas = await getInstructorIdData(instructorId);
            setInstructorClass(clas);
            if (clas.length > 0) {
                setSelectedClass(clas[0].id);
            }
        } catch (error) {
            console.error('Error fetching instructor data:', error);
            setError('Failed to load instructor data');
        }
    };

    useEffect(() => {
        const fetchClassDetail = async () => {
            if (selectedClass && instructorId) {
                try {
                    const classinfo = await getDataWithClass(selectedClass, instructorId);
                    setClassDetail(classinfo);
                } catch (error) {
                    console.error('Error fetching class detail:', error);
                }
            }
        };
        fetchClassDetail();
    }, [selectedClass, instructorId]);

    useEffect(() => {
        if (isDarkMode && isOpen) {
            const style = document.createElement('style');
            style.id = 'instructor-task-modal-ckeditor-dark';
            style.textContent = `
                .modal-content .ck-editor__editable,
                .modal-content .ck-content,
                .modal-content .ck-content p,
                .modal-content .ck-content span,
                .modal-content .ck-content li,
                .modal-content .ck-content h1,
                .modal-content .ck-content h2,
                .modal-content .ck-content h3 {
                    color: #fff !important;
                }
                .modal-content .ck-editor__editable {
                    background-color: #2a2d35 !important;
                }
            `;
            document.head.appendChild(style);
            return () => {
                const existingStyle = document.getElementById('instructor-task-modal-ckeditor-dark');
                if (existingStyle) existingStyle.remove();
            };
        }
    }, [isDarkMode, isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (category === 'Student') {
                if (selectedStudent === 'All') {
                    await Promise.all(
                        instructorStudent.map(async (data) => {
                            await addData({
                                from: data.instructorId,
                                to: data.studentId,
                                subject: taskTopic,
                                setDate: date,
                                desc: desc
                            });
                        })
                    );
                } else {
                    await addData({
                        from: instructorId,
                        to: selectedStudent,
                        subject: taskTopic,
                        setDate: date,
                        desc: desc
                    });
                }
            } else {
                await Promise.all(
                    classDetail.map(async (data) => {
                        await addData({
                            from: data.instructorId,
                            to: data.studentId,
                            subject: taskTopic,
                            setDate: date,
                            desc: desc
                        });
                    })
                );
            }

            if (onSave) await onSave();
            
            showToastMessage('Task added successfully!');
            setTimeout(() => toggle(), 2000);
        } catch (error) {
            console.error('Error adding task:', error);
            setError('Failed to add task');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" className={isDarkMode ? 'dark-modal' : ''}>
            <ModalHeader toggle={toggle}>Add New Item</ModalHeader>
            <ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
            <ModalBody>
                {error && <Alert color="danger">{error}</Alert>}
                <form onSubmit={handleSubmit} id="instructor-task-form">
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="form-control-label">Category</Label>
                                <div className="d-flex gap-4">
                                    <div className="form-check">
                                        <Input
                                            type="radio"
                                            name="category"
                                            checked={category === 'Student'}
                                            onChange={() => setCategory('Student')}
                                        />
                                        <Label className="form-check-label">Student</Label>
                                    </div>
                                    <div className="form-check">
                                        <Input
                                            type="radio"
                                            name="category"
                                            checked={category === 'Class'}
                                            onChange={() => setCategory('Class')}
                                        />
                                        <Label className="form-check-label">Class</Label>
                                    </div>
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">{category === 'Student' ? 'Students' : 'Class'}</Label>
                                <Input
                                    type="select"
                                    value={category === 'Student' ? selectedStudent : selectedClass}
                                    onChange={(e) => {
                                        if (category === 'Student') setSelectedStudent(e.target.value);
                                        else setSelectedClass(e.target.value);
                                    }}
                                    required
                                >
                                    {category === 'Student' ? (
                                        <>
                                            <option value="All">All</option>
                                            {instructorStudent.map((data, ix) => (
                                                <option key={ix} value={data.studentId}>{data.name + ' ' + data.surname}</option>
                                            ))}
                                        </>
                                    ) : (
                                        instructorClass.map((data, ix) => (
                                            <option key={ix} value={data.id}>{data.class_name}</option>
                                        ))
                                    )}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Date</Label>
                                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="form-control-label">Topic Type</Label>
                                <div className="d-flex gap-4 mb-2">
                                    <div className="form-check">
                                        <Input type="radio" checked={topicType === 'Grade911'} onChange={() => setTopicType('Grade911')} />
                                        <Label className="form-check-label">Grade 9-11</Label>
                                    </div>
                                    <div className="form-check">
                                        <Input type="radio" checked={topicType === 'Grade12'} onChange={() => setTopicType('Grade12')} />
                                        <Label className="form-check-label">Grade 12</Label>
                                    </div>
                                </div>
                                {topicType === 'Grade911' ? (
                                    <TaskTopic911Select value={taskTopic} setValue={setTaskTopic} isDarkMode={isDarkMode} />
                                ) : (
                                    <TaskTopic12Select value={taskTopic} setValue={setTaskTopic} isDarkMode={isDarkMode} />
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="form-control-label">Description</Label>
                                <TextEditor value={desc} setValue={setDesc} />
                            </FormGroup>
                        </Col>
                    </Row>
                </form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" type="submit" form="instructor-task-form" disabled={loading}>
                    {loading ? 'Adding...' : 'Add Item'}
                </Button>
                <Button color="secondary" onClick={toggle} disabled={loading}>Cancel</Button>
            </ModalFooter>
        </Modal>
    );
};

export default InstructorTaskModal;
